import React, {useCallback, useEffect, useState } from 'react';
import { Layout, Tabs, Typography, Avatar, Tooltip, Card, Empty, Button, Modal } from 'antd';
import moment from 'moment';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faReply } from "@fortawesome/free-solid-svg-icons";
import styles from './Inbox.module.scss';
import ChatSection from '../common/ChatSection/ChatSection';
import {
    buildTimestampedUploadFileName,
    convertLocalToServerTime,
    deleteFromDigitalOceanSpaces,
    DIGITALOCEAN_PUBLIC_URL,
    errorToast,
    infoToast,
    isMobile,
    successToast,
    uploadToDigitalOceanSpaces,
} from '../../api/Util';
import { deleteComment, editChatComment, getInboxMessages, handleInboxMessageStatus, seenMessages, storeChatMessage, storeInboxMessage } from '../../services/inboxService';
import MessageList from './Messages/MessageList';
import PageHeader from '../TopHeader/PageHeader';
import Profile from '../img/profile.png';
import { ADMIN, CUSTOMER, TEAM_LEAD } from '../../api/Constants';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import * as actions from "../../actions/index";
import * as _ from 'lodash';
import ConfirmationDialog from "../ConfirmationDialog";
import { getChatByMessageId } from '../../services/inboxService';
import AddMessageModal from '../common/AddMessageModal/AddMessageModal';
import BackArrowIcon from '../../components/img/back_arrow.png';
import {Inbox_LOADING, INBOX_SET_STATE, Inbox_UPDATED_COUNT, SET_ADD_TITLE, SET_CHAT_TITLE, SET_INBOX_ADD_MESSAGE, SET_INBOX_HAS_MORE, SET_INBOX_MESSAGE, SET_ROWS_COUNT } from "../../actions/types";

const INITIAL_REPLY_STATE = {
    comment: "",
    showReplyField: false,
    parentId: null,
    oldComment: "",
}

const INITIAL_COMMENT_STATE = {
    id: 0, 
    data: "", 
    oldComment: "",
}

const Inbox = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [filterMessages, setFilterMessages] = useState({ message_title: "" });
    const [status, setStatus] = useState("ACTIVE");
    const [state, setState] = useState({ comments: {}, commentsArray: [] });
    const [replyState, setReplyState] = useState(INITIAL_REPLY_STATE);
    const [comment, setComment] = useState(INITIAL_COMMENT_STATE);
    const [deleteConfirmation, setDeleteConfirmation] = useState({ open: false, comment: {} });
    const [disableBtn, setDisableBtn] = useState(false);
    const [offset, setOffset] = useState(0);
    const [attachments, setAttachments] = useState([]);
    const [chatAttachments, setChatAttachments] = useState([]);
    const [mediaPreview, setMediaPreview] = useState({ open: false, url: null, type: null });
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [isSubmittingMessage, setIsSubmittingMessage] = useState(false);

    const props = useSelector((state) => state);
    const { page, chatTitle, addTitle, loading, inboxAddMessage, inboxMessage, currentSelectedMessage, inboxHasMore, inboxState } = useSelector((state) => state.inboxSeenMessages);
    
    const dispatch = useDispatch();
    const loggedInUser = props.auth?.loggedInUser;
    const unSeenMessagesCount = props.inboxSeenMessages?.unSeenCount;
    const limit = 10;

    const getFileKey = useCallback((file) => {
        if (!file) return "";
        return `${file.name || ""}__${file.size || 0}__${file.lastModified || 0}`;
    }, []);

    const filterOutDuplicateFiles = useCallback((files, existingItems = []) => {
        const existingKeys = new Set(
            (existingItems || [])
                .map(item => getFileKey(item?.file))
                .filter(Boolean)
        );
        const next = [];
        for (const file of files || []) {
            const key = getFileKey(file);
            if (!key || existingKeys.has(key)) continue;
            existingKeys.add(key);
            next.push(file);
        }
        return next;
    }, [getFileKey]);

    const cleanupUploads = useCallback((items = []) => {
        (items || []).forEach((a) => {
            try {
                if (a?.uploading && a?.upload?.abort) {
                    a.upload.abort();
                }
            } catch (e) { /* noop */ }
            try {
                if (a?.previewUrl) {
                    URL.revokeObjectURL(a.previewUrl);
                }
            } catch (e) { /* noop */ }
        });
    }, []);

    const deleteObjectFromBucket = useCallback((key) => {
        if (!key) return;
        try {
            deleteFromDigitalOceanSpaces({ key });
        } catch (e) {
            // eslint-disable-next-line no-console
            console.warn("Failed to delete object from bucket:", e);
        }
    }, []);

    useEffect(() => {
        if (unSeenMessagesCount > 0 && (loggedInUser.client_type === TEAM_LEAD || loggedInUser.client_type === ADMIN)) {
            handleSeenMessages();
            dispatch(actions.unSeenMessages());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unSeenMessagesCount])

    const handleSeenMessages = async () => {
        await seenMessages(props.inboxSeenMessages?.unseenMessages);
    }
    
    useEffect(() => {
        if (isMobile) {
            setIsChatOpen(false);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMobile])

    const uploadFileToBucket = useCallback(({
        attachmentIndex,
        file,
        keyPrefix,
        setItems,
    }) => {
        if (!file || !loggedInUser) {
            return;
        }
        const uploadFileName = buildTimestampedUploadFileName(file);
        const key = `${keyPrefix}${loggedInUser.client_id}/${uploadFileName}`;
        const upload = uploadToDigitalOceanSpaces({
            file,
            key,
            onComplete: (err, data) => {
            setItems(prev => {
                const next = [...prev];
                if (!next[attachmentIndex]) return prev; // removed while upload in-flight

                if (err) {
                    if (err.code !== 'RequestAbortedError') {
                        errorToast("Upload failed, please try again");
                    }
                    next[attachmentIndex] = {
                        ...next[attachmentIndex],
                        uploading: false,
                        upload: null,
                    };
                    return next;
                }

                const url = data?.Key ? `${DIGITALOCEAN_PUBLIC_URL}/${data.Key}` : (data?.Location || null);
                next[attachmentIndex] = {
                    ...next[attachmentIndex],
                    url,
                    progress: 100,
                    uploading: false,
                    upload: null,
                };
                return next;
            });
            },
            onProgress: (percentage) => {
                setItems(prev => {
                    const next = [...prev];
                    if (next[attachmentIndex]) {
                        next[attachmentIndex] = {
                            ...next[attachmentIndex],
                            progress: percentage,
                            uploading: true,
                        };
                    }
                    return next;
                });
            },
        });

        // store upload reference (for abort)
        setItems(prev => {
            const next = [...prev];
            if (next[attachmentIndex]) {
                next[attachmentIndex] = {
                    ...next[attachmentIndex],
                    upload,
                    key,
                };
            }
            return next;
        });

    }, [loggedInUser]);

    const addAttachments = useCallback(({
        files,
        existingItems,
        setItems,
        keyPrefix,
    }) => {
        if (!files || !files.length) return;

        const dedupedFiles = filterOutDuplicateFiles(files, existingItems);
        if (!dedupedFiles.length) return;

        const startIndex = (existingItems || []).length;
        const newItems = dedupedFiles.map(file => ({
            file,
            previewUrl: URL.createObjectURL(file),
            progress: 0,
            uploading: true,
            url: null,
        }));

        setItems(prev => [...prev, ...newItems]);

        dedupedFiles.forEach((file, idx) => {
            uploadFileToBucket({
                attachmentIndex: startIndex + idx,
                file,
                keyPrefix,
                setItems,
            });
        });
    }, [filterOutDuplicateFiles, uploadFileToBucket]);

    const removeAttachmentAtIndex = useCallback(({
        index,
        setItems,
    }) => {
        setItems(prev => {
            const attachment = prev[index];
            if (attachment) {
                if (attachment.uploading && attachment.upload) {
                    attachment.upload.abort();
                }
                if (attachment.key) {
                    deleteObjectFromBucket(attachment.key);
                }
                if (attachment.previewUrl) {
                    URL.revokeObjectURL(attachment.previewUrl);
                }
            }
            return prev.filter((_, i) => i !== index);
        });
    }, [deleteObjectFromBucket]);

    const handleImageSelect = (files) => {
        addAttachments({
            files,
            existingItems: attachments,
            setItems: setAttachments,
            keyPrefix: "inbox/",
        });
    };

    const handleRemoveAttachment = (index) => {
        removeAttachmentAtIndex({ index, setItems: setAttachments });
    };

    const handleChatImageSelect = (files) => {
        addAttachments({
            files,
            existingItems: chatAttachments,
            setItems: setChatAttachments,
            keyPrefix: "inbox-chat/",
        });
    };

    const handleRemoveChatAttachment = (index) => {
        removeAttachmentAtIndex({ index, setItems: setChatAttachments });
    };

    const handleOk = async () => {
        if (isSubmittingMessage) return;

        if (isModalOpen.status === 'Re-open') {
            handleInboxMessage(isModalOpen.id, isModalOpen.status);
        } else {
            const hasUploading = attachments.some(a => a.uploading);
            if (hasUploading) {
                return infoToast("Please wait for attachments to finish uploading");
            }
            setIsSubmittingMessage(true);
            try {
                const imageUrls = attachments.map(a => a.url).filter(Boolean);
                const messageFor = filterMessages.view_as || loggedInUser?.client_id;
                let obj = {
                    title: addTitle,
                    latestMessage: inboxAddMessage,
                    status: 'ACTIVE',
                    messageFor,
                };
                if (imageUrls.length) {
                    obj = {
                        ...obj,
                        media_urls: imageUrls,
                    };
                }
                const data = await storeInboxMessage(obj);
                if (data.status) {
                    successToast(data.message);
                    getChatInboxMessages();
                    setIsModalOpen(false);
                } else {
                    errorToast(data.error);
                }
            } catch (error) {
                errorToast("Failed to create message. Please try again.");
            } finally {
                setIsSubmittingMessage(false);
            }
        }
    }

    const handleInboxMessage = async (id, status) => {
        if (isSubmittingMessage) return;
        let obj = {
            id,
            status,
        }
        if (status === 'Re-open') {
            const hasUploading = attachments.some(a => a.uploading);
            if (hasUploading) {
                return infoToast("Please wait for attachments to finish uploading");
            }
            setIsSubmittingMessage(true);
            try {
                const imageUrls = attachments.map(a => a.url).filter(Boolean);
                const messageFor = filterMessages.view_as || loggedInUser?.client_id;
                obj = {
                    ...obj,
                    latestMessage: inboxAddMessage,
                    messageFor,
                };
                if (imageUrls.length) {
                    obj = {
                        ...obj,
                        media_urls: imageUrls,
                    };
                }
                const data = await handleInboxMessageStatus(obj);
                if (data.status) {
                    setState({ comments: {}, commentsArray: [] });
                    dispatch({ type: SET_CHAT_TITLE, payload: "" });
                    successToast(data.message);
                    getChatInboxMessages();
                    setIsModalOpen(false);
                } else {
                    errorToast(data.error);
                }
            } catch (error) {
                errorToast("Failed to update message. Please try again.");
            } finally {
                setIsSubmittingMessage(false);
            }
        } else {
            const data = await handleInboxMessageStatus(obj);
            if (data.status) {
                setState({ comments: {}, commentsArray: [] });
                dispatch({ type: SET_CHAT_TITLE, payload: "" });
                successToast(data.message);
                getChatInboxMessages();
            } else {
                errorToast(data.error);
            }
        }
    }
    useEffect(() => {
        dispatch({type: SET_INBOX_MESSAGE, payload: []});
        dispatch({type: Inbox_LOADING, payload: true});
        getChatInboxMessages();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterMessages, page])

    const handleTabChange = (newStatus) => {
        setStatus(newStatus);
        dispatch({ type: SET_CHAT_TITLE, payload: "" });
        setFilterMessages({ message_title: "" });
        setOffset(0);
        dispatch({type: INBOX_SET_STATE, payload: {comments: {}, commentsArray: []}});
    };
    
    const getChatInboxMessages = async () => {
        let filters = {
            limit: limit,
            page: page - 1,
            title: filterMessages.message_title,
            status: status,
        }
        if (filterMessages.view_as) {
            filters = {
                ...filters,
                view_as: filterMessages.view_as
            }
        }
        const response = await getInboxMessages(filters);
        if (response.status) {
            dispatch({ type: SET_INBOX_MESSAGE, payload: response.data.messages });
            dispatch({ type: SET_ROWS_COUNT, payload: response.data.count });
            dispatch({ type: Inbox_LOADING, payload: false });
        } else {
            dispatch({ type: SET_INBOX_MESSAGE, payload: []});
            dispatch({ type: Inbox_LOADING, payload: false });
        }
    }

    const handleCancel = () => {
        attachments.forEach(a => a?.key && deleteObjectFromBucket(a.key));
        chatAttachments.forEach(a => a?.key && deleteObjectFromBucket(a.key));
        cleanupUploads(attachments);
        cleanupUploads(chatAttachments);
        setIsModalOpen(false);
    }

    const clearFields = () => {
        cleanupUploads(attachments);
        cleanupUploads(chatAttachments);
        dispatch({ type: SET_ADD_TITLE, payload: "" });
        dispatch({ type: SET_INBOX_ADD_MESSAGE, payload: "" });
        setAttachments([]);
        setChatAttachments([]);
    }

    const getChatById = async (calledBy = "noCall", id, offset) => {
        dispatch({ type: Inbox_LOADING, payload: true });
        let obj = {
            id
        };
        if (calledBy === 'scrollCalled') {
            obj = {
                offset,
                currentSelectedMessage
            }
        }
        const data = await getChatByMessageId(calledBy, obj);
        const groupByChat = data.data.groupByChat;
        const commentArray = data.data.comments;
        const count = data.data.count;
        if (offset + commentArray.length === count) {
            dispatch({type: SET_INBOX_HAS_MORE, payload: false});
        }
        setOffset(offset + commentArray.length);
        dispatch({type: INBOX_SET_STATE, payload : {comments: groupByChat ,
            commentsArray: commentArray}})
        dispatch({ type: Inbox_LOADING, payload: false });
    }

    
    function updateCommentGroups(prevComments, newComments) {
		let newTempComments = {};
		if (Object.keys(prevComments).length === 0) {
			newTempComments = newComments;
		} else {
			newTempComments = mergeTwoObjects(prevComments, newComments);
		}
		return newTempComments;
	}

	function mergeTwoObjects(object1, object2) {
		for (let x in object1) {
			for (let y in object2) {
				if (x === y) {
					object1[x] = object1[x].concat(object2[y]);
					delete(object2[y]);
				}
			}
		}

		if (Object.keys(object2).length > 0) {
			for (let y in object2) {
				object1[y] = object2[y];
			}
		}

		return object1;
	}

    const isImageUrl = useCallback((url = "") => /\.(png|jpe?g|gif|webp|bmp|svg)(\?.*)?$/i.test(url), []);
    const isVideoUrl = useCallback((url = "") => /\.(mp4|webm|ogg|mov|avi|wmv|flv|mkv)(\?.*)?$/i.test(url), []);

    const mapComments = (comments, commentsArray) => {
        return Object.keys(comments)?.map(objKey => {
            return comments[objKey]?.map((cmnt, key) => {
                let annotationData;
                const mediaUrls = Array.isArray(cmnt?.media_urls) ? cmnt.media_urls.filter(Boolean) : [];
                if (cmnt.note_type && cmnt.note_type === "annotation") {
                    annotationData = JSON.parse(cmnt.note);
                }
                let temp = commentsArray?.find(element => element.note_id === cmnt.note_parent_id);
                return (
                    <div style={{ width: '100%' }}>
                        {key === 0 && (
                            <div className={styles.chatDateDiv}>
                                <span className={styles.chatDate}>
                                    {new Date(objKey).getDate()} {moment(objKey).format('MMM')} {new Date(objKey).getFullYear()}
                                </span>
                            </div>
            )}
                    <div className={styles.optionAndCommentContainer}>
                    <div className={styles.rowWrapLeft}>
                            <div>
                                {cmnt.profile_img ?
                                    <img src={Profile} alt="avatar"
                                        className={styles.roundImgStyle}
                                        style={{ pointerEvents: 'none', userSelect: "none", verticalAlign: "middle", width: "36px", height: "36px", borderRadius: "50%", marginTop: "2.9rem" }} />
                                    : <Avatar className={styles.avatarStyle}>
                                        {cmnt.firstname || cmnt.lastname ? `${cmnt.firstname.toUpperCase().charAt(0)}${cmnt.lastname.toUpperCase().charAt(0)}` : 'N/A'}
                                    </Avatar>
                                }
                            </div>
                            <div className={styles.commentAndTimeContainer}>
                                {<span className={styles.clientName}><b>{cmnt.firstname} {cmnt.lastname}</b></span>}
                                <div className={styles.commentMessage}>
                                    <div className={styles.chatMessage}>
                                        {temp?.note && <>
                                            <FontAwesomeIcon style={{ color: '#616C76', marginRight: '4px' }} icon={faReply} />
                                        <span>Replied on {(temp.note).substring(0, 15) + '...'}</span><br />
                                        </>}
                                        {
                                            annotationData && annotationData?.data !== "" ? annotationData.data.text :
                                                cmnt.note
                                        }
                                        {mediaUrls.length > 0 && (
                                            <div className={styles.mediaGrid}>
                                                {mediaUrls.map((url, i) => (
                                                    <div key={`${cmnt.note_id}-media-${i}`} className={styles.mediaItem}>
                                                        {isImageUrl(url) ? (
                                                            <button
                                                                type="button"
                                                                className={styles.mediaClickable}
                                                                onClick={() => setMediaPreview({ open: true, url, type: 'image' })}
                                                            >
                                                                <img className={styles.mediaImage} src={url} alt="attachment" />
                                                            </button>
                                                            ) : isVideoUrl(url) ? (
                                                                <button
                                                                    type="button"
                                                                    className={styles.mediaClickable}
                                                                    onClick={() => setMediaPreview({ open: true, url, type: 'video' })}
                                                                >
                                                                    <video className={styles.mediaVideo} src={url} muted />
                                                                    <div className={styles.videoPlayOverlay}>▶</div>
                                                                </button>
                                                        ) : (
                                                            <a href={url} target="_blank" rel="noopener noreferrer">
                                                                {url}
                                                            </a>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        <div className={styles.editAndDeleteIconWrapper}>
                                            <Tooltip placement="bottom" title={'Reply'}>
                                                <FontAwesomeIcon className={styles.replyIcon}
                                                    onClick={() => {
                                                        document.getElementById("submitBtn").focus();
                                                        setReplyState(prev => ({ ...prev, showReplyField: true, parentId: cmnt.note_id, oldComment: cmnt.note }));
                                                    }} icon={faReply} />
                                            </Tooltip>
                                            {
                                                (loggedInUser.client_id === cmnt.note_by_id || (loggedInUser.client_type === ADMIN && cmnt.client_type !== CUSTOMER)) ?
                                                    <>
                                                        <Tooltip placement="bottom" title={'Edit'}>
                                                            <EditOutlined key={'comment-edit-key-id-' + cmnt.note_id}
                                                                onClick={() => {
                                                                    let comment = cmnt.note;
                                                                    comment = cmnt.note;
                                                                    setComment({id: cmnt.note_id, data: comment, oldComment: cmnt.note});
                                                                    const existing = Array.isArray(cmnt?.media_urls) ? cmnt.media_urls.filter(Boolean) : [];
                                                                    setChatAttachments(existing.map((url) => ({
                                                                        url,
                                                                        previewUrl: null,
                                                                        progress: 100,
                                                                        uploading: false,
                                                                        upload: null,
                                                                    })));
                                                                }} className={styles.editIcon} />
                                                        </Tooltip>
                                                        <Tooltip placement="bottom" title={'Delete'}>
                                                            <DeleteOutlined key={'comment-delete-key-id-' + cmnt.note_id}
                                                                onClick={() => setDeleteConfirmation({ open: true, comment: cmnt })} className={styles.deleteIcon} />
                                                        </Tooltip>
                                                    </>
                                                : null
                                            }
                                        </div>
                                    </div>
                                    {/* <p>It's more important to me that you get started on the NEXT videos</p> */}
                                    <span>{moment(cmnt.date_added).format('LT')}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                  )
            })
          
        });
    }

    // when user press enter the comment will send
    function keyPressHandler(e, calledBy = 'main') {
        if (!e.shiftKey && e.key === 'Enter') {
            e.preventDefault();
            if (replyState.showReplyField) {
                send('reply');
            } else {
                if (comment.id > 0) {
                    editComment();
                } else {
                    send(calledBy);
                }
            }
        }
        e.persist();
    }

    async function send(calledBy = 'main') {
        let note = calledBy === "reply" ? replyState.comment : comment.data;

        const imageUrls = chatAttachments.map(a => a.url).filter(Boolean);

        if ((!note || note === '') && (!imageUrls || imageUrls.length === 0)) {
            return;
        }
        if (disableBtn) {
            return infoToast("A new comment request has been already made for this comment");
        }
        if (chatAttachments.some(a => a.uploading)) {
            return infoToast("Please wait for attachments to finish uploading");
        }

        infoToast("Creating a new comment request");
        setDisableBtn(true);

        let obj = {
            chat_id: currentSelectedMessage,
            note: note || ""
        }
        if (imageUrls.length) {
            obj = {
                ...obj,
                media_urls: imageUrls,
            };
        }
        if (calledBy === "reply") {
            obj = {
                ...obj,
                noteParentId: replyState.parentId,
            }
        }
        const data = await storeChatMessage(obj);
        dispatch({type:Inbox_UPDATED_COUNT, payload: data.count});
        let comments = inboxState.comments;
        if (calledBy === "reply") {
            console.log('object');
            let newComment = {
                note_id: data.id,
                note: note,
                media_urls: imageUrls,
                date_added: convertLocalToServerTime(new Date()),
                note_by_id: loggedInUser.client_id,
                firstname: loggedInUser.firstname,
                lastname: loggedInUser.lastname,
                note_parent_id: replyState.parentId
            };
            comments = filterComments(comments, newComment, "reply");
        } else {
            let checkDate = moment().format("YYYY-MM-DD")
            let newList = {
                note_id: data.id,
                note: note,
                media_urls: imageUrls,
                date_added: convertLocalToServerTime(new Date()),
                note_by_id: loggedInUser.client_id,
                firstname: loggedInUser.firstname,
                lastname: loggedInUser.lastname,
            };
            let newArray = [];
            let obj;
            let flag = false;
            Object.keys(comments || {}).forEach(objKey => {
                if (objKey === checkDate) {
                    flag = true;
                    comments[objKey].unshift(newList);
                }
            });
            let dateAdded = newList.date_added;
            if (!flag) { 
                newArray.push(newList);
                 obj ={
                    [dateAdded]: newArray
                }
            }
            comments = {...obj, ...comments} 
        }
        dispatch({
            type: INBOX_SET_STATE,
            payload: {...inboxState, comments:comments}
        });
        setComment(INITIAL_COMMENT_STATE);
        setReplyState(INITIAL_REPLY_STATE);
        setChatAttachments([]);
        successToast("Comment is added successfully");
        setDisableBtn(false);
    }

    async function handleDeleteComment(comment) {
        if (!comment) {
            return;
        }
        let obj = {
            id: comment.note_id,
            chat_id: currentSelectedMessage,
        }
        const data = await deleteComment(obj);
        getChatById('noCall', currentSelectedMessage, offset);
        dispatch({type: Inbox_UPDATED_COUNT, payload: data.count});
        successToast(data.message);
    }

    async function editComment() {
        let note = comment.data;
        let id = comment.id;
        let chatId = currentSelectedMessage;

        const mediaUrls = chatAttachments.map(a => a.url).filter(Boolean);

        if ((!note || note === '') && (!mediaUrls || mediaUrls.length === 0)) {
            return;
        }
        if (disableBtn) {
            return infoToast("A new edit comment request has been already made for this comment");
        }
        if (chatAttachments.some(a => a.uploading)) {
            return infoToast("Please wait for attachments to finish uploading");
        }
        infoToast("Creating a new edit comment request");
        setDisableBtn(true);

        let obj = {
            note: note || "",
            id,
            chatId,
            media_urls: mediaUrls,
        }
        const data = await editChatComment(obj);
        if (data.status) {
            let comments = inboxState.comments;
            comments = filterComments(comments, { noteId: id, note: note, media_urls: mediaUrls }, "edit");
            dispatch({type: INBOX_SET_STATE, payload: {...inboxState, comments: comments}})
            setComment(INITIAL_COMMENT_STATE);
            setChatAttachments([]);
            successToast(data.message ?? "Comment edited successfully");
            getChatInboxMessages();
        }
        else {
            errorToast("Unable to edit comment, try again later");
        }
        setDisableBtn(false);
    }

    function filterComments(comments, newComment, type) {
        let flag = false;
        Object.keys(comments || {}).forEach(objKey => {
            if (type === "reply" && !flag) {
                // eslint-disable-next-line no-unused-expressions
                let result = comments[objKey]?.filter(cmnt => cmnt.note_id === newComment.note_parent_id);
                if (result.length > 0) {
                    flag = true;
                    comments[objKey].unshift(newComment);
                }
            } else if (type === "edit") {
                // eslint-disable-next-line no-unused-expressions
                comments[objKey]?.filter(comment => comment.note_id === newComment.noteId).map(comment => {
                    comment.note = newComment.note;
                    if (Array.isArray(newComment.media_urls)) {
                        comment.media_urls = newComment.media_urls;
                    }
                    return comment;
                });
            }
        })
        return comments;
    }

    function loopLoadComments(calledBy = "scrollCalled") {
        getChatById('scrollCalled', currentSelectedMessage, offset);
    }

    const cardClick = () => {
        if (isMobile) {
            setIsChatOpen(true);
        }
    }

    return (
        <Layout className={styles.CardDetailsLayout}>
            <ConfirmationDialog
                onNegative={() => { setDeleteConfirmation({ open: false }) }}
                onPositive={() => handleDeleteComment(deleteConfirmation.comment)}
                confirmationHeader="Confirm"
                confirmationDialogId={"confirm-move"}
                confirmation={deleteConfirmation.open}
                confirmationText={"Are you sure you want to delete this comment?"}
            />
            <PageHeader title="Inbox" />
            <div className={`${styles.wrapper} ${isMobile && isChatOpen ? styles.mobileChatOpen : ""}`}>
                <div className={styles.tabsContainer}>
                    <Tabs tabBarStyle={{color: '#ab322d'}} className='inbox-tabs' onChange={handleTabChange}>
                        <Tabs.TabPane tab="Active" key="ACTIVE">
                            <MessageList
                                status={status}
                                filterMessages={filterMessages}
                                setFilterMessages={setFilterMessages}
                                setIsModalOpen={setIsModalOpen}
                                getChatById={getChatById}
                                getChatInboxMessages={getChatInboxMessages}
                                handleInboxMessage={handleInboxMessage}
                                cardClick={cardClick}
                            />
                        </Tabs.TabPane>
                        <Tabs.TabPane tab="Archived" key="RESOLVED">
                            <MessageList
                                status={status}
                                filterMessages={filterMessages}
                                setFilterMessages={setFilterMessages}
                                setIsModalOpen={setIsModalOpen}
                                getChatById={getChatById}
                                getChatInboxMessages={getChatInboxMessages}
                                handleInboxMessage={handleInboxMessage}
                                cardClick={cardClick}
                            />
                        </Tabs.TabPane>
                    </Tabs>
                </div>
                <div id="chatSectionId" className={styles.chatSectionContainer}>
                    <Button
                            type="link"
                            className={`${styles.greyFontColor} ${styles.arrowIconAndTextWrapper} ${styles.deskMode}`}
                            onClick={() => setIsChatOpen(false)}
                        >
                        <img src={BackArrowIcon} alt="BackArrowIcon" /> Back
                    </Button>
                    <Typography.Title className={styles.title}>
                        {chatTitle}
                    </Typography.Title>
                    {
                        inboxState.commentsArray.length > 0 &&
                            <ChatSection
                                state={inboxState}
                                teamState={[]}
                                chatView={{value: 'inboxChat'}}
                                loopLoadComments={loopLoadComments}
                                mapComments={mapComments}
                                isLoading={loading}
                                replyState={replyState}
                                setReplyState={setReplyState}
                                INITIAL_REPLY_STATE={INITIAL_REPLY_STATE}
                                comment={comment}
                                setComment={setComment}
                                video={""}
                                keyPressHandler={keyPressHandler}
                                editComment={editComment}
                                send={send}
                                disableBtn={disableBtn}
                                hasMore={inboxHasMore}
                                chatSectionType={"inboxChat"}
                                chatAttachments={chatAttachments}
                                onSelectChatFiles={handleChatImageSelect}
                                onRemoveChatAttachment={handleRemoveChatAttachment}
                                isUploadingChatImages={chatAttachments.some(a => a.uploading)}
                            />
                    }
                    {inboxMessage.length > 0 && inboxState.commentsArray.length === 0 && !loading &&
                        <Card className={styles.cards}>
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={"No message selected"} />
                        </Card>
                    }
                </div>
            </div>
            <AddMessageModal
                title="New message"
                isModalOpen={isModalOpen}
                handleOk={handleOk}
                handleCancel={handleCancel}
                clearFields={clearFields}
                attachments={attachments}
                onSelectFiles={handleImageSelect}
                onRemoveAttachment={handleRemoveAttachment}
                isUploading={attachments.some(a => a.uploading)}
                isSubmitting={isSubmittingMessage}
            />

            <Modal
                open={mediaPreview.open}
                onCancel={() => setMediaPreview({ open: false, url: null, type: null })}
                footer={null}
                centered
                width={900}
                destroyOnClose
                className={styles.mediaPreviewModal}
            >
                {mediaPreview.type === 'image' && mediaPreview.url && (
                    <img className={styles.mediaPreviewImage} src={mediaPreview.url} alt="preview" />
                )}
                {mediaPreview.type === 'video' && mediaPreview.url && (
                    <video 
                        className={styles.mediaPreviewVideo} 
                        src={mediaPreview.url} 
                        controls 
                        autoPlay
                    />
                )}
            </Modal>
        </Layout>
    )
}

export default Inbox;
